import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'flowbite-react';
import AttachmentDynamicModalProps from "./AttachmentDynamicModalProps";
import { Media } from '../../types/media';
import { eventEmitter, EventMap } from "../../helpers/eventEmitter";
import { ZoomHandler, ZoomMode, cursorImage } from '../../helpers/zoomHelpers'
import PaginationControls from './RenderFileFooter';

export const RenderDocument = ({
    media,
    file_obj,
    disabled,
    onRemove,
    component,
    getFile
}: AttachmentDynamicModalProps) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const fileContainerRef = useRef<HTMLImageElement>(null);
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const [selectedPagePreviewImage, setSelectedPagePreviewImage] = useState<string | null>(null);
    const [status, setStatus] = useState<string>('');
    const [scale, setScale] = useState(1);
    const [page, setPage] = useState(0);
    const [pages, setPages] = useState<number | null>(null);
    const [zoomMode, setZoomMode] = useState(ZoomMode.IN);
    const modalBodyClass = 'w-full h-full flex flex-col justify-center items-center overflow-auto';

    const openModal = () => {
        setScale(1);
        setStatus('Загрузка документа...');

        // Получить файл вне компонента
        let fileUrl: Media | undefined;
        const mediaId = media?.id

        if (mediaId && getFile !== undefined) {
            fileUrl = getFile(mediaId);
        }
        if (fileUrl) {
            setSelectedFile(fileUrl.file);
            fetch(fileUrl.file)
                .then((response) => response.arrayBuffer())
                .then((arrayBuffer) => {
                    const previews = media?.previews

                    if (previews && previews.length > 0) {
                        setStatus('');
                        setPages(previews.length)
                        setPage(1)
                    } else {
                        setStatus('Документ готовится для просмотра');
                    }
                })
                .catch((e) => {
                    console.error(e);
                    setStatus('Ошибка загрузки документа');
                });
        }
    };

    const closeModal = () => {
        setSelectedFile(null);
        setStatus('');
        setScale(1);
        setZoomMode(ZoomMode.IN)
    };

    useEffect(() => {
        const previews = media?.previews;

        if (previews && previews[page - 1]) {
            setStatus('');
            const preview = previews[page - 1].file;
            setSelectedPagePreviewImage(preview);
        } else {
            setStatus('Ошибка загрузки документа');
        }
    }, [page]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                closeModal();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Обновить открытое модальное окно после получения previews
    useEffect(() => {
        const handleDocumentAction = (payload: EventMap['document']) => {
            const matchingMedia = payload.media?.find(item => item.id === media?.id);
            if (!matchingMedia) {
                return;
            }

            switch (payload.action) {
                case 'documentPreviewIsReady':
                    if (!modalRef.current) {
                        return
                    }

                    openModal()
                    break;
            }
        };

        eventEmitter.on('document', handleDocumentAction);

        return () => {
            eventEmitter.off('document', handleDocumentAction);
        };
    }, [media]);

    function applyZooms(scale: number, event: React.MouseEvent<HTMLImageElement, MouseEvent>) {
        const container = modalRef?.current?.getElementsByClassName(modalBodyClass)[0];
        const content = fileContainerRef?.current;

        if (container && content) {
            new ZoomHandler().applyZooms(
                scale,
                event,
                container,
                content
            )
        } else {
            console.error('Container or content not found');
        }
    }

    const handleImageClick = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        switch (zoomMode) {
            case ZoomMode.DISABLE:
                break
            case ZoomMode.IN:
                const newScaleValue = scale + 1;
                setScale(newScaleValue);
                applyZooms(newScaleValue, event);
                setZoomMode(ZoomMode.OUT)
                break
            case ZoomMode.OUT:
                if (scale > 1) {
                    const newScale = scale - 1;
                    setScale(newScale);
                    applyZooms(newScale, event);
                    setZoomMode(ZoomMode.IN)
                }
                break
        }
    };

    return (
        <>
            {media && component({
                media: media,
                onClick: openModal
            })}
            {file_obj &&
                component({
                    file_obj: file_obj,
                    disabled: disabled,
                    onRemove: onRemove,
                    onClick: openModal
                })}
            {/* Модальное окно на весь экран для файла */}
            <Modal
                size={'6xl'}
                show={selectedFile !== null}
                ref={modalRef}
                style={{
                    transformOrigin: '0 0'
                }}
                onClose={closeModal}>
                <Modal.Header className="overflow-hidden whitespace-pre-wrap break-word-only-long">
                    {media && media.original_name}
                    {file_obj && file_obj.media_info.original_name}
                </Modal.Header>
                <Modal.Body
                    className={modalBodyClass}
                    style={{
                        transformOrigin: '0 0'
                    }}
                >
                    {selectedPagePreviewImage && <img
                        src={selectedPagePreviewImage}
                        ref={fileContainerRef}
                        alt=''
                        onClick={handleImageClick}
                        style={{
                            transform: `scale(${scale})`,
                            transformOrigin: '0 0',
                            width: 'auto',
                            height: 'auto',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            position: 'relative',
                            cursor: cursorImage(zoomMode)
                        }}
                        className="min-h-[20vh] min-w-[20vw] max-h-[75vh] max-w-[80vw] object-contain"
                    />}
                    {status && <div>{status}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <PaginationControls
                        page={page}
                        pages={pages ?? 0}
                        setPage={setPage}
                        media={media}
                        file_obj={file_obj}
                    />
                </Modal.Footer>
            </Modal>
        </>
    );
};